/**
 * Cute animal drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_doodles`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const cuteAnimalDrawingsPack: ToilePack = {
    slug: 'cute-animal-drawings',
    category: 'coloring',
    name: 'Cute animal drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'bunny', label: 'Bunny', subject: 'a fluffy bunny sitting up with one ear flopped down, holding a small carrot in its front paws'},
        {key: 'fox', label: 'Fox', subject: 'a little fox curled in a ball, its big tail wrapped around its nose and its eyes closed'},
        {key: 'panda', label: 'Panda', subject: 'a baby panda sitting and chewing a bamboo stalk, round ears and dark eye patches'},
    ],
    styles: [
        {key: 'kawaii', label: 'Round and cute', style: 'a thick round outline, soft rounded shapes, big shiny eyes and a tiny smile, no shading', palette: 'Black ink only, no gray tones.', framing: 'One drawing centered on the page with a wide white margin.'},
        {key: 'pencil', label: 'Soft pencil', style: 'a soft pencil outline with light fur strokes and a little graphite shading', palette: 'Graphite gray on white paper.', framing: 'One drawing centered on the page with a wide white margin.'},
        {key: 'pastel', label: 'Pastel fill', style: 'a clean black outline with flat pastel fills and a pink blush on the cheeks', palette: 'Pastel fills inside the black outline, the ground pure white.', framing: 'One drawing centered on the page with a wide white margin.'},
    ],
    directions: ['a duck', 'a hedgehog', 'with a hat'],
    directionHint: 'try: a duck',
    seo: {
        title: 'Cute Animal Drawings: Bunny, Fox, Panda',
        metaDescription: 'Nine cute animal drawings, a bunny, a fox and a baby panda, one animal to a page, in round outline, soft pencil and pastel fill.',
        h1: 'Cute animal drawings: bunny, fox, panda',
        tagline: 'One animal to a page, centered on white and drawn larger than a doodle.',
        intro: 'Cute animal drawings are single animals drawn larger and with more care than a doodle, as a page to copy or color. These nine pages show one animal each, centered on white: a bunny, a fox and a baby panda. Each comes as a round black outline, soft pencil with fur strokes, or a clean outline with pastel fills. For a different animal, type it in the direction box.',
        useCases: [
            'A copy page for a young artist',
            'A card or gift tag illustration',
            'A coloring page for a child',
            'A sticker design to trace',
        ],
        faq: [
            {q: 'Why is there one animal per page?', a: 'A single animal gives the page room to copy the outline and the details at a comfortable size.'},
            {q: 'Is the pencil style shaded?', a: 'It has light fur strokes and a little graphite shading, with the outline kept clean.'},
            {q: 'Can I ask for a different animal?', a: 'Yes. Type it in the direction box, such as a duck, a hedgehog or with a hat. A typed animal is a request, so the result may differ from the samples.'},
        ],
    },
}
